<template>
  <div class="confirm-dialog">
    <div class="confirm-header">
      <h3 class="confirm-title">{{ title }}</h3>
      <span v-if="showClose" class="confirm-close" @click="handleClose">×</span>
    </div>
    <div class="confirm-content">{{ content }}</div>
    <div class="confirm-footer">
      <button
        v-if="cancelButton !== false"
        class="confirm-button confirm-button-default"
        @click="handleCancel"
      >
        {{ cancelButtonText }}
      </button>
      <button
        class="confirm-button"
        :class="`confirm-button-${confirmButton.type || 'primary'}`"
        @click="handleConfirm"
      >
        {{ confirmButton.text }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ConfirmModalProps } from './confirm-types';

const props = withDefaults(
  defineProps<ConfirmModalProps>(),
  {
    title: '提示',
    showClose: true,
  }
);

const emit = defineEmits<{
  cancel: [data?: any];
  confirm: [data?: any];
  close: [data?: any];
}>();

const cancelButtonText = computed(() => {
  if (props.cancelButton === false) return '';
  return props.cancelButton?.text || '取消';
});

const handleCancel = async () => {
  let result: any = undefined;
  if (props.cancelButton !== false && props.cancelButton?.onClick) {
    result = await props.cancelButton.onClick();
  }
  emit('cancel', result);
};

const handleConfirm = async () => {
  let result: any = undefined;
  if (props.confirmButton?.onClick) {
    result = await props.confirmButton.onClick();
  }
  emit('confirm', result);
};

const handleClose = () => {
  emit('close');
};
</script>

<style lang="scss" scoped>
.confirm-dialog {
  min-width: 416px;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.confirm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.confirm-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101729;
}

.confirm-close {
  font-size: 24px;
  line-height: 1;
  color: #8c8c8c;
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: #262626;
  }
}

.confirm-content {
  padding: 24px;
  font-size: 14px;
  line-height: 22px;
  color: #101729;
  word-break: break-word;
}

.confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 24px 16px;
}

.confirm-button {
  min-width: 72px;
  height: 32px;
  padding: 4px 16px;
  font-size: 14px;
  line-height: 22px;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.3s;
  outline: none;

  &:active {
    transform: translateY(1px);
  }
}

.confirm-button-default {
  background: #ffffff;
  border-color: #d9d9d9;
  color: #262626;

  &:hover {
    border-color: #4096ff;
    color: #4096ff;
  }
}

.confirm-button-primary {
  background: #1677ff;
  color: #ffffff;

  &:hover {
    background: #4096ff;
  }
}

.confirm-button-danger {
  background: #ff4d4f;
  color: #ffffff;

  &:hover {
    background: #ff7875;
  }
}

.confirm-button-success {
  background: #52c41a;
  color: #ffffff;

  &:hover {
    background: #73d13d;
  }
}

.confirm-button-warning {
  background: #faad14;
  color: #ffffff;

  &:hover {
    background: #ffc53d;
  }
}
</style>
